import { useEffect, useState } from "react";
import { supabase } from "../../../../../integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../ui/tabs";
import { Skeleton } from "../../../../ui/skeleton";
import { UrgentMetrics } from "./urgent/UrgentMetrics";
import { TicketCard } from "./urgent/TicketCard";

interface UrgentReviewsProps {
  selectedOutlets: string[];
  supabaseoutletIDs?: string[],
}

export const UrgentReviews = ({ selectedOutlets, supabaseoutletIDs }: UrgentReviewsProps) => {
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  const [generatingReplyFor, setGeneratingReplyFor] = useState<string | null>(null);
  const [metrics, setMetrics] = useState({
    activeTickets: 0,
    urgentTickets: 0,
    recoveryRate: 0,
    resolvedThisMonth: 0,
  });

  useEffect(() => {
    loadUrgentReviews();
    calculateMetrics();
  }, [selectedOutlets, activeTab]);

  const loadUrgentReviews = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("reviews")
        .select("*")
        .eq("urgency_flag", true)
        .order("posted_at", { ascending: false, nullsFirst: false });

      // Filter by outlet if not "all"
      const isAllSelected = !selectedOutlets || selectedOutlets.length === 0 || selectedOutlets.includes("all");
      if (!isAllSelected && selectedOutlets.length > 0) {
        query = query.in("outlet_id", selectedOutlets);
      }else {
        query = query.in('outlet_id', supabaseoutletIDs);
      }

      // Filter by status based on active tab
      if (activeTab === "active") {
        query = query.neq("status", "Resolved");
      } else if (activeTab === "resolved") {
        query = query.eq("status", "Resolved");
      }

      const { data, error } = await query;

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error("Error loading urgent reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = async () => {
    try {
      let query = supabase.from("reviews").select("*").eq("urgency_flag", true);

      const isAllSelected = !selectedOutlets || selectedOutlets.length === 0 || selectedOutlets.includes("all");
      if (!isAllSelected && selectedOutlets.length > 0) {
        query = query.in("outlet_id", selectedOutlets);
      }else {
        query = query.in('outlet_id', supabaseoutletIDs);
      }

      const { data, error } = await query;
      if (error) throw error;

      const allUrgent = data || [];
      const active = allUrgent.filter((r) => r.status !== "Resolved");
      const urgent = active.filter((r) => r.urgency === "high");
      const resolved = allUrgent.filter((r) => r.status === "Resolved");

      // Calculate recovery rate
      const recoveryRate = allUrgent.length > 0
        ? Math.round((resolved.length / allUrgent.length) * 100)
        : 0;

      // Calculate resolved this month
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const resolvedThisMonth = resolved.filter(
        (r) => r.resolution_date && new Date(r.resolution_date) >= firstDayOfMonth
      ).length;

      setMetrics({
        activeTickets: active.length,
        urgentTickets: urgent.length,
        recoveryRate,
        resolvedThisMonth,
      });
    } catch (error) {
      console.error("Error calculating metrics:", error);
    }
  };

  const handleGenerateReply = async (reviewId: string, reviewText: string, rating: number, platform: string, authorName: string) => {
    setGeneratingReplyFor(reviewId);
    try {
      const { data, error } = await supabase.functions.invoke('generate-reply', {
        body: { reviewText, rating, platform, authorName }
      });

      if (error) throw error;

      const review = reviews.find(r => r.id === reviewId);
      if (review) {
        review.aiReply = data.reply;
        setReviews([...reviews]);
      }
    } catch (error) {
      console.error('Error generating reply:', error);
    } finally {
      setGeneratingReplyFor(null);
    }
  };

  const handleCopyReply = (reply: string) => {
    navigator.clipboard.writeText(reply);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Urgent Reviews</h2>
        <p className="text-muted-foreground">
          Track, prioritize, and resolve negative reviews with our recovery management system
        </p>
      </div>

      <UrgentMetrics {...metrics} />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="resolved">Resolved</TabsTrigger>
          <TabsTrigger value="all">All Tickets</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 w-full" />
              ))}
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-lg">No active urgent reviews</p>
              <p className="text-sm">Great job keeping up with negative feedback!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {reviews.map((review) => (
                <TicketCard
                  key={review.id}
                  review={review}
                  onUpdate={() => {
                    loadUrgentReviews();
                    calculateMetrics();
                  }}
                  onGenerateReply={handleGenerateReply}
                  onCopyReply={handleCopyReply}
                  isGeneratingReply={generatingReplyFor === review.id}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="resolved" className="space-y-4">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 w-full" />
              ))}
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-lg">No resolved tickets yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {reviews.map((review) => (
                <TicketCard
                  key={review.id}
                  review={review}
                  onUpdate={() => {
                    loadUrgentReviews();
                    calculateMetrics();
                  }}
                  onGenerateReply={handleGenerateReply}
                  onCopyReply={handleCopyReply}
                  isGeneratingReply={generatingReplyFor === review.id}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="all" className="space-y-4">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 w-full" />
              ))}
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-lg">No urgent reviews found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {reviews.map((review) => (
                <TicketCard
                  key={review.id}
                  review={review}
                  onUpdate={() => {
                    loadUrgentReviews();
                    calculateMetrics();
                  }}
                  onGenerateReply={handleGenerateReply}
                  onCopyReply={handleCopyReply}
                  isGeneratingReply={generatingReplyFor === review.id}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
